import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, LogIn } from 'lucide-react';
import { api } from '../../utils/api';
import { login as authLogin } from '../../utils/auth';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.login({ email, password });
      authLogin(response.data.token, response.data.user);
      toast.success('Login successful!');
      
      // If there's a redirect path, go there (for booking flow continuity)
      if (redirectPath) {
        navigate(redirectPath);
      } else if (response.data.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9] flex flex-col">
      <Header />
      
      <div className="flex-1 flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8" data-testid="login-form-container">
            <div className="text-center mb-8">
              <h1 className="font-serif text-3xl text-[#3E2723] mb-2">Welcome Back</h1>
              <p className="text-[#786E66]">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none transition-all"
                    placeholder="your.email@example.com"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none transition-all"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full py-3 font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : (
                  <>
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </>
                )}
              </button>

              <div className="text-center">
                <Link to="/forgot-password" className="text-sm text-[#3E2723] hover:underline" data-testid="forgot-password-link">
                  Forgot your password?
                </Link>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-[#786E66]">
                Don't have an account?{' '}
                <Link to={`/signup${redirectPath ? `?redirect=${encodeURIComponent(redirectPath)}` : ''}`} className="text-[#3E2723] font-medium hover:underline" data-testid="login-signup-link">
                  Sign Up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LoginPage;
